import { useEffect, useState, useMemo } from 'react';
import { Todo, TodoPriority } from '../../types/todo';
import { useEventStore } from '../../store/eventStore';
import { useFamilyStore } from '../../store/familyStore';
import { useThemeStore } from '../../store/themeStore';
import { getDesignTokens } from '../../utils/designTokens';
import { formatDate } from '../../utils/time';
import { Icon } from '../../utils/icons';
import { getBaseEventId, isExpandedRecurringInstance } from '../../utils/recurringInstance';
import { useToastStore } from '../../store/toastStore';

interface TodoModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: {
    title: string;
    description?: string;
    dueDateTime?: Date;
    listId?: string;
    priority?: TodoPriority;
    assigneeId?: string;
    assigneeName?: string;
    isFamily: boolean;
    eventId?: string;
    eventTitle?: string;
  }) => Promise<void>;
  editingTodo?: Todo | null;
  lists: Array<{ listId: string; name: string }>;
  isLoading?: boolean;
}

export default function TodoModal({
  isOpen,
  onClose,
  onSubmit,
  editingTodo,
  lists,
  isLoading = false,
}: TodoModalProps) {
  const { isDarkMode } = useThemeStore();
  const tokens = getDesignTokens(isDarkMode);
  const { showToast } = useToastStore();
  const { events, fetchEvents } = useEventStore();
  const { family } = useFamilyStore();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [dueDateTime, setDueDateTime] = useState<string>('');
  const [listId, setListId] = useState<string>('');
  const [priority, setPriority] = useState<TodoPriority>('medium');
  const [assigneeId, setAssigneeId] = useState<string>('family');
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [showEventSearch, setShowEventSearch] = useState(false);
  const [eventSearchQuery, setEventSearchQuery] = useState('');

  const familyMembers = family?.members || [];

  // Fetch events when modal opens
  useEffect(() => {
    if (isOpen) {
      // Fetch events for past 30 days to future 90 days
      const today = new Date();
      const pastDate = new Date(today);
      pastDate.setDate(today.getDate() - 30);
      const futureDate = new Date(today);
      futureDate.setDate(today.getDate() + 90);

      fetchEvents(
        pastDate.toISOString().split('T')[0],
        futureDate.toISOString().split('T')[0]
      ).catch((error) => {
        console.error('Error fetching events:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load events', 'error');
      });
    }
  }, [isOpen, fetchEvents, showToast]);

  // Pre-fill form when editing
  useEffect(() => {
    if (editingTodo) {
      setTitle(editingTodo.title);
      setDescription(editingTodo.description || '');
      setDueDateTime(
        editingTodo.dueDateTime
          ? (editingTodo.dueDateTime instanceof Date 
              ? editingTodo.dueDateTime.toISOString().slice(0, 10)
              : typeof editingTodo.dueDateTime === 'string' && editingTodo.dueDateTime.match(/^\d{4}-\d{2}-\d{2}$/)
              ? editingTodo.dueDateTime
              : new Date(editingTodo.dueDateTime).toISOString().slice(0, 10))
          : ''
      );
      setListId(editingTodo.listId || '');
      setPriority(editingTodo.priority || 'medium');
      setAssigneeId(editingTodo.assigneeId || 'family');
      setSelectedEventId(editingTodo.eventId || '');
      setEventSearchQuery('');
      setShowEventSearch(false);
    } else {
      // Reset form for new todo
      setTitle('');
      setDescription('');
      setDueDateTime('');
      setListId('');
      setPriority('medium');
      setAssigneeId('family');
      setSelectedEventId('');
      setEventSearchQuery('');
      setShowEventSearch(false);
    }
  }, [editingTodo, isOpen]);

  // Filter events: show only base events (not recurring instances) and apply search
  const filteredEvents = useMemo(() => {
    // Filter out recurring instances - only keep base events
    // Use a Map to deduplicate by base ID, keeping the actual base event when available
    const baseEventsMap = new Map<string, typeof events[0]>();
    
    events.forEach((event) => {
      if (isExpandedRecurringInstance(event)) {
        // This is a recurring instance, get the base ID
        const baseId = getBaseEventId(event.id);
        // Only add if we haven't seen this base event yet
        if (!baseEventsMap.has(baseId)) {
          // Try to find the actual base event (not an instance) in the events array
          const baseEvent = events.find(e => e.id === baseId && !isExpandedRecurringInstance(e));
          if (baseEvent) {
            baseEventsMap.set(baseId, baseEvent);
          }
          // If base event not found, don't add the instance - we only want base events
        }
      } else {
        // This is a base event (not a recurring instance)
        baseEventsMap.set(event.id, event);
      }
    });
    
    // Convert map to array
    let result = Array.from(baseEventsMap.values());
    
    // Apply search filter if query exists
    if (eventSearchQuery.trim()) {
      const query = eventSearchQuery.toLowerCase();
      result = result.filter((event) =>
        event.title.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [events, eventSearchQuery]);

  // Format event for display
  const formatEventDisplay = (event: typeof events[0]): string => {
    const dateStr = formatDate(event.startTime, 'short');
    return `${event.title} - ${dateStr}`;
  };

  // Get selected event title
  const selectedEventTitle = useMemo(() => {
    if (!selectedEventId) return undefined;
    const event = events.find((e) => e.id === selectedEventId);
    return event?.title;
  }, [events, selectedEventId]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;

    // For date-only input, create Date at UTC midnight to avoid timezone issues
    const dueDate = dueDateTime ? (() => {
      const [year, month, day] = dueDateTime.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    })() : undefined;
    const assignee = assigneeId === 'family' ? undefined : assigneeId;

    await onSubmit({
      title: title.trim(),
      description: description.trim() || undefined,
      dueDateTime: dueDate,
      listId: listId || undefined,
      priority,
      assigneeId: assignee,
      // assigneeName is not sent - backend will derive it from assigneeId
      isFamily: !assignee,
      eventId: selectedEventId || undefined,
      eventTitle: selectedEventTitle,
    });
  };

  // Handle escape key
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className={`relative ${tokens.card.bg} rounded-landing ${tokens.card.shadow} ${tokens.border.light} border max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col z-10`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${tokens.border.light}`}>
          <h2 className={`text-xl font-bold ${tokens.text.primary}`}>
            {editingTodo ? 'Edit To Do' : 'Create To Do'}
          </h2>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          {/* Title */}
          <div>
            <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              type="text"
              id="title"
              required
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
              placeholder="To do title"
              autoFocus
            />
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent resize-none"
              placeholder="Optional description"
            />
          </div>

          {/* Due Date */}
          <div>
            <label htmlFor="dueDateTime" className="block text-sm font-medium text-gray-700 mb-1">
              Due Date (optional)
            </label>
            <input
              type="date"
              id="dueDateTime"
              value={dueDateTime}
              onChange={(e) => setDueDateTime(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
            />
          </div>

          {/* List Selection */}
          <div>
            <label htmlFor="listId" className="block text-sm font-medium text-gray-700 mb-1">
              List
            </label>
            <select
              id="listId"
              value={listId}
              onChange={(e) => setListId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
            >
              <option value="">No list</option>
              {lists.map((list) => (
                <option key={list.listId} value={list.listId}>
                  {list.name}
                </option>
              ))}
            </select>
          </div>

          {/* Priority */}
          <div>
            <label htmlFor="priority" className="block text-sm font-medium text-gray-700 mb-1">
              Priority
            </label>
            <select
              id="priority"
              value={priority}
              onChange={(e) => setPriority(e.target.value as TodoPriority)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
            >
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>

          {/* Assignee */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Assign To
            </label>
            <div className="flex flex-wrap gap-3">
              {/* Family option */}
              <button
                type="button"
                onClick={() => setAssigneeId('family')}
                disabled={isLoading}
                className={`relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all min-h-[80px] min-w-[80px] disabled:opacity-50 disabled:cursor-not-allowed ${
                  assigneeId === 'family'
                    ? 'border-blue-500 bg-blue-50 scale-105 shadow-sm'
                    : 'border-gray-300 bg-white hover:scale-105 active:scale-95'
                }`}
              >
                <div className="relative">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg bg-blue-500">
                    👥
                  </div>
                  {assigneeId === 'family' && (
                    <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white z-20">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                </div>
                <span className={`text-[10px] font-semibold ${assigneeId === 'family' ? 'text-blue-600' : 'text-gray-700'}`}>
                  Family
                </span>
              </button>
              
              {/* Member options */}
              {familyMembers.map((member) => {
                const isSelected = assigneeId === member.userId;
                return (
                  <button
                    key={member.userId}
                    type="button"
                    onClick={() => setAssigneeId(member.userId || 'family')}
                    disabled={isLoading}
                    className={`relative flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all min-h-[80px] min-w-[80px] disabled:opacity-50 disabled:cursor-not-allowed ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 scale-105 shadow-sm'
                        : 'border-gray-300 bg-white hover:scale-105 active:scale-95'
                    }`}
                  >
                    <div className="relative">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: member.color }}
                      >
                        {member.name.charAt(0).toUpperCase()}
                      </div>
                      {member.avatar && (
                        <img
                          src={member.avatar}
                          alt={member.name}
                          className="absolute inset-0 w-12 h-12 rounded-full object-cover border-2 z-10"
                          style={{ borderColor: member.color }}
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display = 'none';
                          }}
                        />
                      )}
                      {isSelected && (
                        <div className="absolute -top-1 -right-1 w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center border-2 border-white z-20">
                          <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <span className={`text-[10px] font-semibold ${isSelected ? 'text-blue-600' : 'text-gray-700'}`}>
                      {member.name}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Event Link */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label htmlFor="eventId" className="block text-sm font-medium text-gray-700">
                Link to Event
              </label>
              <button
                type="button"
                onClick={() => {
                  setShowEventSearch(!showEventSearch);
                  if (showEventSearch) {
                    setEventSearchQuery('');
                  }
                }}
                className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors"
                aria-label="Filter events"
              >
                <Icon name="filter" size="sm" />
                <span>Filter</span>
              </button>
            </div>

            {/* Event Search Input */}
            {showEventSearch && (
              <div className="mb-2">
                <input
                  type="text"
                  value={eventSearchQuery}
                  onChange={(e) => setEventSearchQuery(e.target.value)}
                  placeholder="Search events by title..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent text-sm"
                />
              </div>
            )}

            <select
              id="eventId"
              value={selectedEventId}
              onChange={(e) => {
                setSelectedEventId(e.target.value);
                if (showEventSearch) {
                  setShowEventSearch(false);
                  setEventSearchQuery('');
                }
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-transparent"
            >
              <option value="">No event</option>
              {filteredEvents.map((event) => (
                <option key={event.id} value={event.id}>
                  {formatEventDisplay(event)}
                </option>
              ))}
            </select>
          </div>

          {/* Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className={`flex-1 ${tokens.card.bg} ${tokens.border.light} border ${tokens.text.primary} font-semibold py-2.5 px-4 rounded-landing transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-80`}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading || !title.trim()}
              className={`flex-1 ${tokens.button.primary} font-semibold py-2.5 px-4 rounded-landing transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoading ? 'Saving...' : editingTodo ? 'Save Changes' : 'Create To Do'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
